'use client';

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { TailwindSpinner } from '@components/common/Elements/Spinner';
import { Button } from '@components/common';
import { useElevenLabsToken } from '../../hooks/useElevenLabsToken';
import ElevenLabsInterviewRoomContent from './ElevenLabsInterviewRoomContent';

interface ElevenLabsInterviewRoomProps {
  locale: Locale;
  onDisconnect: () => void;
  agentId?: string
}

/**
 * ElevenLabsInterviewRoom
 *
 * This component handles the complete audio interview experience using ElevenLabs:
 * - Fetches conversation token when component mounts
 * - Shows loading spinner while fetching token
 * - Renders ElevenLabsInterviewRoomContent when token is ready
 * - Handles errors and shows appropriate UI
 *
 * Props:
 * - locale: Language/locale for the interview
 * - slug: Optional job slug for job-based interview
 * - onDisconnect: Callback when disconnecting from the interview room
 */
const ElevenLabsInterviewRoom: React.FC<ElevenLabsInterviewRoomProps> = ({
  locale,
  onDisconnect,
  agentId,
}) => {
  const { t } = useTranslation('common');
  const {
    conversationToken, isLoading, error, fetchToken,
  } = useElevenLabsToken(locale, agentId);
  const [hasStarted, setHasStarted] = useState(false);

  // reset token when agentId changes
  useEffect(() => {
    setHasStarted(false);
  }, [agentId]);

  // Fetch token on mount
  useEffect(() => {
    if (!hasStarted) {
      setHasStarted(true);
      void fetchToken();
    }
  }, [hasStarted, fetchToken, agentId]);

  // Show loading spinner while fetching token
  if (isLoading || (!conversationToken && !error)) {
    return (
      <div className="flex flex-col items-center justify-center space-y-4 py-12">
        <TailwindSpinner className="size-8" />
      </div>
    );
  }

  // If error occurred, show error message
  if (error || !conversationToken) {
    return (
      <div className="flex flex-col items-center justify-center space-y-4 py-12">
        <p className="text-center text-sm text-gray-500">{t('global.something.went.wrong')}</p>
        <Button
          color="primary"
          onClick={onDisconnect}
        >
          {t('global.action.back')}
        </Button>
      </div>
    );
  }

  // Render the actual interview room with ElevenLabs
  return (
    <ElevenLabsInterviewRoomContent
      locale={locale}
      conversationToken={conversationToken}
      onDisconnect={onDisconnect}
    />
  );
};

export default ElevenLabsInterviewRoom;
